/**
 * One bubble — the union of what the three legacy surfaces rendered.
 *
 * Office messages, office comments, comment-thread replies, pinned rows and the member
 * thread were five near-identical copies of this markup; the only real differences were
 * which actions hung off the hover bar and whether a "Pinned by" strip sat above. Both
 * are props here, which is what collapses the five into one.
 *
 * Author identity (avatar, name) resolves per bubble through the cached
 * `useUserDetail`, so a thread of 25 messages from 3 people costs 3 queries, not 25.
 *
 * No reactions: they ride a real `Bubble`'s own footer, which this row
 * does not draw. A thread shows them once the surface's conversation gate is on and
 * `ConversationMessageItem` renders instead.
 */

import type { GetUsersByUserIdResponse } from "@birdwatch/client";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { useMemo } from "react";
import { useUserDetail } from "@/api/use-user-detail";
import { copyToast } from "@/components/ui/common/copy";
import {
  BwDeleteLine,
  BwLinkLine,
  BwLoadingLine,
  BwPencilLine,
  BwPin1Fill,
  BwPinLine,
  BwPinRemoveLine,
  BwReplyShareLeftLine,
} from "@/components/ui/icons";
import { Markdown } from "@/components/ui/markdown";
import {
  LinkPreviewCard,
  Message,
  MessageAttachment,
  MessageDocumentList,
} from "@/components/ui/message";
import { Typography } from "@/components/ui/typography";
import { AudioRecordAttachment } from "@/features/shared/audio-recorder";
import type { Channel, ChannelMessage } from "../_api/channel-endpoints";
import { useJustArrivedHighlight } from "../_hooks/use-just-arrived-highlight";
import { dedupeMessageBlocks } from "../_libs/dedupe-message-blocks";
import { splitAttachments } from "../_libs/message-attachments";
import { isOptimisticMessage } from "../_libs/optimistic-message";
import {
  type ChannelBlockContext,
  type ChannelMessageAction,
  type ChannelMessageActionsConfig,
  isMessageActionAllowed,
} from "../_types/channel-surface";
import { ChannelAuthorAvatar, ChannelAuthorName } from "./channel-author";
import { ChannelBlock } from "./channel-block";
import { ChannelMessageActions } from "./channel-message-actions";
import { ChannelQuoteCard } from "./channel-quote-card";
import { ChannelReadIndicator } from "./channel-read-indicator";
import { ChannelThreadSummary } from "./channel-thread-summary";

export type ChannelMessageItemProps = {
  cid: string;
  channel: Channel;
  message: ChannelMessage;
  viewerId: string | undefined;
  isUnread: boolean;
  /** Deep-link / pinned-jump scroll target flash. The just-arrived flash is computed internally. */
  isHighlighted?: boolean;
  showReadReceipts: boolean;
  showPinnedBadge: boolean;
  /**
   * Whether this message offers anything to act on: the hover action bar and, in the
   * conversation UI, the reaction cluster it shares a row with. A thread's parent message
   * is shown as CONTEXT above the replies, so it renders with this off — the body stays,
   * the affordances go. Defaults to on.
   */
  showActions?: boolean;
  actionsConfig: ChannelMessageActionsConfig;
  renderBlock?: (context: ChannelBlockContext) => React.ReactNode;
  renderAvatar?: (context: { userId: string }) => React.ReactNode;
  renderUserName?: (context: { userId: string }) => React.ReactNode;
  onEdit?: (message: ChannelMessage) => void;
  onDelete?: (message: ChannelMessage) => void;
  onTogglePin?: (message: ChannelMessage) => void;
  /** Quote the message in the composer. Absent where there is no composer to quote into. */
  onReply?: (message: ChannelMessage) => void;
  /** Scroll to the message a reply quotes. Absent, the quote card is not clickable. */
  onOpenQuotedMessage?: (messageId: string) => void;
  className?: string;
};

export function ChannelMessageItem({
  cid,
  channel,
  message,
  viewerId,
  isUnread,
  isHighlighted = false,
  showReadReceipts,
  showPinnedBadge,
  showActions = true,
  actionsConfig,
  renderBlock,
  renderAvatar,
  renderUserName,
  onEdit,
  onDelete,
  onTogglePin,
  onReply,
  onOpenQuotedMessage,
  className,
  ...rest
}: ChannelMessageItemProps) {
  // Already in cache — the avatar and name resolve the same row.
  const { data: author } = useUserDetail(message.userCreatedId, {
    enabled: !!message.userCreatedId,
  });
  // Resolves the pinner from the same cached useUserDetail rows as the author/avatar.
  const { data: pinner } = useUserDetail(message.pinnedById ?? "", {
    enabled: !!message.pinnedById,
  });

  const isOwnMessage = message.userCreatedId === viewerId;
  // Flashes once when someone else's message arrives seconds ago; OR'd with the deep-link flash below.
  const isJustArrived = useJustArrivedHighlight(
    message.createdAt,
    isOwnMessage,
  );
  const isPinned = !!message.pinnedAt;
  // A placeholder waiting for the server — dimmed, uninteractable, and tagged
  // `data-status="pending"` (the convention the office bubbles used, and a hook for e2e).
  const isPending = isOptimisticMessage(message);

  const { audioAttachments, mediaAttachments, documentAttachments } = useMemo(
    () => splitAttachments(message.attachments),
    [message.attachments],
  );

  // The same block written twice (a retried accept, a V4 + channel double write) is one
  // card, exactly as the prompt endpoint counts it — see `dedupeMessageBlocks`.
  const blocks = useMemo(
    () => dedupeMessageBlocks(message.blocks),
    [message.blocks],
  );

  // What this message answers ("Quote reply") — drawn above its text.
  const quotedMessage = message.quotedMessage;

  const actions = useMemo<ChannelMessageAction[]>(() => {
    // Derived, so it is built inside the memo — hoisting it would give the memo a new
    // object identity on every render and defeat it entirely.
    const messageContext = { message, channel, isOwnMessage };
    const built: ChannelMessageAction[] = [];

    if (actionsConfig.copyLink) {
      built.push({
        title: "Copy link",
        icon: BwLinkLine,
        onClick: () => copyToast(actionsConfig.copyLink!(messageContext)),
      });
    }

    if (isMessageActionAllowed(actionsConfig.edit, messageContext) && onEdit) {
      built.push({
        title: "Edit",
        icon: BwPencilLine,
        onClick: () => onEdit(message),
      });
    }

    // After Edit, with the conversation bubble's icon — `conversation/_libs/message-actions.ts`
    // — so the action reads the same whichever bubble the gate serves.
    if (
      isMessageActionAllowed(actionsConfig.reply, messageContext) &&
      onReply
    ) {
      built.push({
        title: "Quote reply",
        icon: BwReplyShareLeftLine,
        onClick: () => onReply(message),
      });
    }

    if (actionsConfig.pin && onTogglePin) {
      built.push({
        title: isPinned ? "Unpin" : "Pin",
        icon: isPinned ? BwPinRemoveLine : BwPinLine,
        onClick: () => onTogglePin(message),
      });
    }

    if (
      isMessageActionAllowed(actionsConfig.delete, messageContext) &&
      onDelete
    ) {
      built.push({
        title: "Delete",
        icon: () => <BwDeleteLine className="text-text-negative" size={20} />,
        onClick: () => onDelete(message),
      });
    }

    return [...built, ...(actionsConfig.extra?.(messageContext) ?? [])];
  }, [
    actionsConfig,
    channel,
    isOwnMessage,
    isPinned,
    message,
    onDelete,
    onEdit,
    onReply,
    onTogglePin,
  ]);

  const avatar = renderAvatar?.({ userId: message.userCreatedId }) ?? (
    <ChannelAuthorAvatar userId={message.userCreatedId} />
  );
  const userName = renderUserName?.({ userId: message.userCreatedId }) ?? (
    <ChannelAuthorName userId={message.userCreatedId} />
  );

  return (
    <div
      data-message-id={message.id}
      data-old-message-id={message.oldId ?? undefined}
      data-status={isPending ? "pending" : undefined}
      className={cn(isPending && "opacity-60 pointer-events-none")}
    >
      {showPinnedBadge && isPinned && (
        <div
          className={cn(
            /* sorted */
            "flex items-center gap-1",
            "pt-2 px-3 xl:px-4 pl-14",
            "text-text-dark-low",
          )}
        >
          <BwPin1Fill size={14} />
          <Typography size="xs" className="text-text-dark-low">
            {pinner?.firstName ? `Pinned by ${pinner.firstName}` : "Pinned"}
          </Typography>
        </div>
      )}
      <Message
        avatar={avatar}
        userCreateName={userName}
        viaClient={message.viaClient ?? ""}
        createdAt={message.createdAt}
        updatedAt={message.updatedAt}
        isNew={isUnread}
        isHighlight={isHighlighted || isJustArrived}
        renderReadReceipts={
          showReadReceipts
            ? () => (
                <ChannelReadIndicator
                  cid={cid}
                  message={message}
                  className="hidden md:flex"
                />
              )
            : undefined
        }
        className={cn(
          /* sorted */
          "group",
          "transition-colors duration-1000 ease-out hover:bg-bg-dark-min",
          className,
        )}
        {...rest}
      >
        {quotedMessage && (
          <ChannelQuoteCard
            quote={quotedMessage}
            onOpen={
              onOpenQuotedMessage
                ? () => onOpenQuotedMessage(quotedMessage.id)
                : undefined
            }
            className="mt-1 mb-2"
          />
        )}

        {!isEmpty(mediaAttachments) && (
          <MessageAttachment attachments={mediaAttachments as never} />
        )}

        <Markdown content={message.body ?? ""} className="text-sm" />

        {audioAttachments.map((attachment) => (
          <AudioRecordAttachment
            key={attachment?.id}
            // The channel item reuses the shipped `attachmentSchema`, so the row is
            // structurally identical; the player's prop type still names the office
            // message type it was written against.
            attachment={attachment as never}
            userCreated={author as GetUsersByUserIdResponse}
          />
        ))}

        {!isEmpty(documentAttachments) && (
          <MessageDocumentList attachments={documentAttachments as never} />
        )}

        {blocks.map((block) => (
          <ChannelBlock
            key={block.id}
            block={block}
            message={message}
            channel={channel}
            renderBlock={renderBlock}
            // This bubble stacks its children with no gap, so the space between cards is
            // its own to ask for. The cards no longer assume it.
            cardClassName="mt-3"
          />
        ))}

        {message.linkPreview && (
          <LinkPreviewCard linkPreview={message.linkPreview} className="mt-3" />
        )}

        {message.replyCount > 0 && actionsConfig.onOpenThread && (
          <ChannelThreadSummary
            message={message}
            onOpen={() => actionsConfig.onOpenThread?.(message)}
            className="mt-1"
          />
        )}

        {isPending && (
          <div className="flex items-center gap-1 mt-1 text-text-dark-min">
            <BwLoadingLine size={12} className="animate-spin" />
            <span className="text-xs">Sending…</span>
          </div>
        )}

        {showActions && <ChannelMessageActions actions={actions} />}
      </Message>
    </div>
  );
}
